import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Activity, Briefcase, Gift, BarChart3,
  Building2, UserCog, Sliders, ChevronDown, LogOut, KeyRound, Check, Upload, RefreshCw, MailOpen,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";

const CORE = [
  { name: "Dashboard", to: "/dashboard", icon: LayoutDashboard, key: "dashboard" },
  { name: "Contacts", to: "/contacts", icon: Users, key: "contacts" },
  { name: "Re-Engaged", to: "/contacts?stage=RE_ENGAGED", icon: RefreshCw, key: "reengaged" },
  { name: "Activity", to: "/activity", icon: Activity, key: "activity" },
  { name: "Deals", to: "/deals", icon: Briefcase, key: "deals" },
  { name: "Referrals", to: "/referrals", icon: Gift, key: "referrals" },
  { name: "Reports", to: "/reports", icon: BarChart3, key: "reports" },
  { name: "Email Marketing", to: "/email-marketing", icon: MailOpen, key: "email-marketing" },
];
const ADMIN = [
  { name: "Clients", to: "/clients", icon: Building2, key: "clients" },
  { name: "Users", to: "/users", icon: UserCog, key: "users" },
];

const LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_1f616cd5-79d5-4fb8-bbee-901e55ebdd90/artifacts/xmy3jilq_brand-partners-logo.png";

function NavItem({ item, forceActive }) {
  return (
    <NavLink
      to={item.to}
      data-testid={`nav-${item.key}`}
      className={({ isActive }) => {
        const active = forceActive !== undefined ? forceActive : isActive;
        return `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          active ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
        }`;
      }}
    >
      <item.icon className="h-[18px] w-[18px]" />
      {item.name}
    </NavLink>
  );
}

export function Sidebar({ onNavigate }) {
  const { user, logout, isSuperAdmin, isReadOnly } = useAuth();
  const { clients, selected, setSelectedClient, isAll } = useClient();
  const navigate = useNavigate();
  const location = useLocation();
  const isReeng = location.pathname === "/contacts" && new URLSearchParams(location.search).get("stage") === "RE_ENGAGED";

  const currentLabel = isAll ? "All Clients" : clients.find((c) => c.id === selected)?.business_name || "Select Client";

  return (
    <div className="h-full flex flex-col bg-[#0B0F17] sidebar-scroll">
      {/* Logo */}
      <div className="bg-white px-5 pt-5 pb-4 border-b border-slate-200">
        <div className="flex items-center justify-center">
          <img src={LOGO} alt="Brand Partners" className="h-8 object-contain" />
        </div>
        <div className="mt-2.5 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold text-center">
          Virtual Sales Management
        </div>
      </div>

      {/* Client selector */}
      <div className="px-4 pt-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Current Client</div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="client-selector-dropdown"
              className="w-full flex items-center justify-between gap-2 bg-[#151C28] border border-slate-700/60 rounded-lg px-3 py-2.5 text-sm font-semibold text-white hover:border-slate-600 transition-colors"
            >
              <span className="truncate">{currentLabel}</span>
              <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60" align="start">
            <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isSuperAdmin && (
              <DropdownMenuItem data-testid="client-option-all" onClick={() => setSelectedClient("all")}>
                <span className="flex-1 font-semibold">All Clients</span>
                {isAll && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
            )}
            {clients.map((c) => (
              <DropdownMenuItem key={c.id} data-testid={`client-option-${c.id}`} onClick={() => setSelectedClient(c.id)}>
                <span className="flex-1 truncate">{c.business_name}</span>
                {selected === c.id && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" onClick={onNavigate}>
        {CORE.map((i) => {
          let forceActive;
          if (i.key === "contacts") forceActive = location.pathname === "/contacts" && !isReeng;
          else if (i.key === "reengaged") forceActive = isReeng;
          return <NavItem key={i.key} item={i} forceActive={forceActive} />;
        })}
        {!isReadOnly && <NavItem item={{ name: "Import", to: "/import", icon: Upload, key: "import" }} />}
        <NavItem item={{ name: "Settings", to: "/settings", icon: Sliders, key: "settings" }} />
        {isSuperAdmin && (
          <>
            <div className="px-3 pt-5 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-600">Administration</div>
            {ADMIN.map((i) => <NavItem key={i.key} item={i} />)}
          </>
        )}
      </nav>

      {/* User */}
      <div className="border-t border-slate-800/70 p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger" className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors">
              <div className="h-9 w-9 rounded-full bg-blue-600 grid place-items-center text-white font-bold text-sm shrink-0">
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 text-left">
                <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
                <div className="text-[11px] text-slate-500 truncate capitalize">{user?.role?.replace("_", " ")}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" side="top">
            <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="change-password-menu" onClick={() => navigate("/change-password")}>
              <KeyRound className="h-4 w-4 mr-2" /> Change Password
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-rose-600">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
